import cloneDeep from 'lodash/cloneDeep';
import { env } from '../../env';
import { DeleteStageType } from '../../models/DeleteStageType';
import {
    GridConfig,
    RequestOptions,
    Stage,
    StageHeader,
    StageOperationEnum,
    StageStatusEnum,
    ToastTypeEnum,
    Widget,
} from '../../models/Index';
import HttpService from '../httpService/HttpService';
import StateService from '../stateService/StateService';

const httpClient = HttpService.getAxiosClient();

// Base Url
const UD_BACKEND_URL = `${env.REACT_APP_BACKEND_URL}/v1`;

function getStages(customerUuid: string, options?: RequestOptions): Promise<StageHeader[]> {
    const url = `${UD_BACKEND_URL}/stage-headers`;
    return httpClient
        .get(url, { ...options, headers: { customerUuid } })
        .then((response) => response.data)
        .catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_STAGES_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({ source: 'STAGE_SERVICE.GET_STAGES', text: msg, type: ToastTypeEnum.ERROR });
        });
}

function getStage(stageId: string, customerUuid: string, options?: RequestOptions): Promise<Stage> {
    const url = `${UD_BACKEND_URL}/stages/${stageId}`;
    return httpClient
        .get(url, { ...options, headers: { customerUuid } })
        .then((response) => response.data)
        .catch((err) => {
            /*const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_STAGE_DATA_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({ source: 'STAGE_SERVICE.GET_STAGE', text: msg, type: ToastTypeEnum.ERROR });*/
            return null;
        });
}

function patchStage(stage: Stage, customerUuid: string, operationType: StageOperationEnum, options?: RequestOptions): Promise<any> {
    const url = `${UD_BACKEND_URL}/stages/${stage.stageId}`;
    let propertiesToPatch = {};
    // Save exiting stage
    if ([StageOperationEnum.SAVE].includes(operationType)) {
        propertiesToPatch = {
            workInProgressGridConfig: stage.workInProgressGridConfig,
            workInProgressWidgetData: stage.workInProgressWidgetData,
            title: stage.title,
        };
    }
    // Publish exiting stage
    if ([StageOperationEnum.PUBLISH].includes(operationType)) {
        propertiesToPatch = {
            publishedGridConfig: stage.publishedGridConfig,
            publishedWidgetData: stage.publishedWidgetData,
            title: stage.title,
        };
    }

    return httpClient
        .patch(url, propertiesToPatch, { ...options, headers: { customerUuid } })
        .then((response) => {
            StateService.toastMessage$.next({
                source: 'STAGE_SERVICE.PATCH_STAGE',
                text: 'TOAST_MESSAGE.STAGE_SUCCESSFULLY_UPDATED_TEXT',
                type: ToastTypeEnum.SUCCESS,
            });
            return response;
        })
        .catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_UPDATING_STAGE_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({ source: 'STAGE_SERVICE.PATCH_STAGE', text: msg, type: ToastTypeEnum.ERROR });
        });
}

function postStage(body: Stage, customerUuid: string, options?: RequestOptions): Promise<any> {
    if (body.status) {
        delete body.status;
    }
    const url = `${UD_BACKEND_URL}/stages`;
    return httpClient
        .post(url, body, { ...options, headers: { customerUuid } })
        .then((response) => {
            StateService.toastMessage$.next({
                source: 'STAGE_SERVICE.POST_STAGE',
                text: 'TOAST_MESSAGE.STAGE_SUCCESSFULLY_SAVED_TEXT',
                type: ToastTypeEnum.SUCCESS,
            });
            return response;
        })
        .catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_SAVING_STAGE_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({ source: 'STAGE_SERVICE.POST_STAGE', text: msg, type: ToastTypeEnum.ERROR });
        });
}

function deleteStage(stageId: string, customerUuid: string, options?: RequestOptions): Promise<any> {
    const url = `${UD_BACKEND_URL}/stages/${stageId}`;
    return httpClient
        .delete(url, { ...options, headers: { customerUuid } })
        .then((response) => {
            StateService.toastMessage$.next({
                source: 'STAGE_SERVICE.DELETE_STAGE',
                text: 'TOAST_MESSAGE.STAGE_SUCCESSFULLY_REMOVED_TEXT',
                type: ToastTypeEnum.SUCCESS,
            });
            return response.status;
        })
        .catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_DELETING_STAGE_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({ source: 'STAGE_SERVICE.DELETE_STAGE', text: msg, type: ToastTypeEnum.ERROR });
        });
}

async function saveStage(
    body: Stage,
    stageHeader: StageHeader[],
    selectedHeader: StageHeader,
    customerUuid: string,
    operationType: StageOperationEnum,
    options?: RequestOptions,
) {
    if (body.workInProgressGridConfig?.layouts?.lg?.length > 0) {
        body = StageService.setGridLockState(body, body.workInProgressGridConfig?.layouts?.lg, 'workInProgressGridConfig', true);
    }
    let response;
    if (body.stageId) {
        response = await patchStage(body, customerUuid, operationType, options);
        if (response?.status === 200) {
            let newStatus = '' as StageStatusEnum;
            if ([StageOperationEnum.SAVE].includes(operationType)) {
                newStatus = [StageStatusEnum.SAVED].includes(body.status) ? StageStatusEnum.SAVED : StageStatusEnum.PUBLISHED_AND_EDITED;
            }
            if ([StageOperationEnum.PUBLISH].includes(operationType)) {
                newStatus = StageStatusEnum.PUBLISHED;
            }
            if (body.workInProgressGridConfig?.layouts?.lg?.length > 0) {
                body = StageService.setGridLockState(body, body.workInProgressGridConfig?.layouts?.lg, 'workInProgressGridConfig', false);
            }
            const savedStage = body as Stage;
            savedStage.status = newStatus;
            StateService.savedStage$.next(cloneDeep(savedStage));
            StateService.selectedStage$.next(savedStage);
            const stageId = savedStage.stageId;
            let header = stageHeader.filter((header) => header.stageId === stageId)[0];
            [header, selectedHeader].forEach((item) => {
                item.status = newStatus;
            });
            StateService.stageHeader$.next(stageHeader);
            StateService.selectedHeader$.next(selectedHeader);
        }
    } else {
        response = await postStage(body, customerUuid, options);
        if (response.status === 200) {
            let savedStage = response.data as Stage;
            if (body.workInProgressGridConfig?.layouts?.lg?.length > 0) {
                savedStage = StageService.setGridLockState(
                    savedStage,
                    savedStage.workInProgressGridConfig?.layouts?.lg,
                    'workInProgressGridConfig',
                    false,
                );
            }
            savedStage.status = (response.data as Stage)?.status;
            StateService.savedStage$.next(cloneDeep(savedStage));
            StateService.selectedStage$.next(savedStage);
            const draftId = selectedHeader.draftId;
            const stageId = savedStage.stageId;
            let header = stageHeader.filter((header) => header.draftId === draftId)[0];
            // Update status, add stageId, delete draftId for selectedHeader and item in Header-Array
            [header, selectedHeader].forEach((item) => {
                item.stageId = stageId;
                item.status = StageStatusEnum.SAVED;
                delete item.draftId;
            });
            StateService.stageHeader$.next(stageHeader);
            StateService.selectedHeader$.next(selectedHeader);
        }
    }
    return response;
}

function getStagesForLogin(username: string, customerUuid: string, options?: RequestOptions): Promise<StageHeader[]> {
    const url = `${UD_BACKEND_URL}/logins/${username}/stage-headers`;
    return httpClient
        .get(url, { ...options, headers: { customerUuid } })
        .then((response) => response.data)
        .catch((err) => {
            const defaultText = 'TOAST_MESSAGE.ERROR_REQUESTING_STAGES_FOR_LOGIN_TEXT';
            const msg = err.response?.data ? err.response.data : defaultText;
            StateService.toastMessage$.next({ source: 'STAGE_SERVICE.GET_STAGES_FOR_LOGIN', text: msg, type: ToastTypeEnum.ERROR });
        });
}

function sortHeaderArrayAlphabetically(array: StageHeader[]): StageHeader[] {
    const headerArray = array.length > 0 ? ([...array].sort((a, b) => a.title.localeCompare(b.title)) as StageHeader[]) : [];
    return headerArray;
}

function setGridLockState(stage: Stage, widgets: Widget[], key: string, isGridLocked: boolean): Stage {
    let tempStage = cloneDeep(stage) as Stage;
    const tempWidgets = cloneDeep(widgets);
    tempWidgets.forEach((item) => (item.static = isGridLocked));
    tempStage[key].layouts.lg = tempWidgets;
    return tempStage;
}

function getNewStage(): Stage {
    const initConfig = {
        layouts: {
            lg: [],
        },
        compactType: null,
        currentBreakpoint: 'lg',
        breakpoints: { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 },
        cols: { lg: 12, md: 8, sm: 4, xs: 2, xxs: 1 },
        rowHeight: 75,
        preventCollision: true,
        /* sets the margin on x and y axis */
        margin: [24, 24],
        /* sets the padding for the entire container on x and y axis */
        containerPadding: [2, 24],
    } as GridConfig;
    return {
        granularityLevel: 1,
        publishedGridConfig: initConfig,
        publishedWidgetData: null,
        status: StageStatusEnum.NEW,
        title: '',
        workInProgressGridConfig: initConfig,
        workInProgressWidgetData: [],
    } as Stage;
}

const StageService = {
    deleteStage,
    getNewStage,
    getStage,
    getStages,
    getStagesForLogin,
    httpClient,
    patchStage,
    postStage,
    saveStage,
    setGridLockState,
    sortHeaderArrayAlphabetically,
};

export default StageService;
